import React, { useMemo, useState } from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import useTablePaginationAndSort from 'hooks/useTablePaginationAndSort';
import { getHeaderSortClassName } from 'helpers/tableSort';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import FormattedNumber from 'components/FormattedNumber';
import EmptyTableState from 'components/EmptyTableState';
import useTableScrollConfig from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useTableScrollConfig';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import { PlanProductLineItem } from '../types';
import PlanProductViewMoreDrawer from './PlanProductViewMoreDrawer';

const NOT_AVAILABLE = '---';
const PAGE_SIZE = 20;

/**
 * The View More column's cell — hover state matches Applications/Work
 * Orders' own `LinkCell`. `whitespace-nowrap` keeps the label from wrapping
 * once the column shrinks, so the table's horizontal scroll
 * (`tableLayout="fixed"`) takes over instead.
 */
const ViewMoreCell: React.FC<{
  product: PlanProductLineItem;
  onViewMore: () => void;
}> = ({ product, onViewMore }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="flex justify-center" style={{ whiteSpace: 'nowrap' }}>
      <button
        type="button"
        aria-label={`View more details for ${product.productName}`}
        // Inline `whiteSpace: 'nowrap'` on both the button and the label —
        // the Tailwind `whitespace-nowrap` class alone was still wrapping
        // onto two lines, so this pins it with a specificity nothing else
        // in this column can beat.
        style={{ whiteSpace: 'nowrap' }}
        className={`flex items-center gap-1 rounded py-10px px-8px ${
          isHovered ? 'bg-blue-10' : ''
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onViewMore}
      >
        <OpenInNewIcon
          className={`shrink-0 ${
            isHovered ? 'text-blue-50' : 'text-neutral-60'
          }`}
        />
        <span
          style={{ whiteSpace: 'nowrap' }}
          className={isHovered ? 'text-blue-50' : 'text-dark-grey'}
        >
          View More
        </span>
      </button>
    </div>
  );
};
const PRODUCTS_TABLE_SCROLL_X = 500;
// No tank-info section on Plans (FR-019a) — same leading height/offset the
// Invoices Products tab uses for its own no-tank-info case, since both now
// share the same overall chrome shape (two header stat cards + card-style
// tabs) the shared hook's fixed chrome-reserve constants were calibrated
// against.
const PRODUCTS_TABLE_OFFSET_EM = 39.75;
const PRODUCTS_TABLE_LEADING_HEIGHT = 8;

export interface PlanProductsTabProps {
  products: PlanProductLineItem[];
}

/**
 * Products tab body — self-contained table mirroring
 * `InvoiceProductsTab`'s pattern rather than Applications/Work Orders'
 * `ProductsTab`, which couples to tank-info/target-pest concepts Plans lacks.
 */
const PlanProductsTab: React.FC<PlanProductsTabProps> = ({ products }) => {
  const {
    reactUiKitTablePaginationProp,
    reactUiKitTableOnChange,
    sortBy,
    sortDir,
    page
  } = useTablePaginationAndSort({ pageSize: PAGE_SIZE }) as any;

  // FR-082: "View More" per-product drawer.
  const [
    productUnderView,
    setProductUnderView
  ] = useState<PlanProductLineItem | null>(null);

  const productRowCountOnPage = useMemo(() => {
    if (products.length === 0) return 0;
    const start = (page - 1) * PAGE_SIZE;
    return Math.min(Math.max(products.length - start, 0), PAGE_SIZE);
  }, [products.length, page]);

  const hasPaginationNavigation = products.length > PAGE_SIZE;

  const columns = useMemo(
    () => [
      {
        title: 'Product Name',
        dataIndex: 'productName',
        key: 'productName',
        width: 200,
        fixed: true,
        className: getHeaderSortClassName('productName', sortBy, sortDir),
        sorter: (a: PlanProductLineItem, b: PlanProductLineItem) =>
          a.productName.localeCompare(b.productName),
        sortOrder: sortBy === 'productName' && sortDir,
        render: (_: unknown, product: PlanProductLineItem) => (
          <>
            <div className="text-neutral-90 font-medium truncate">
              {product.productName}
            </div>
            {product.manufacturerName && (
              <div className="text-neutral-60 text-xs truncate">
                {product.manufacturerName}
              </div>
            )}
          </>
        )
      },
      {
        title: <span className="block w-full text-right">Rate per Acre</span>,
        dataIndex: 'rate',
        key: 'rate',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'rate',
          sortBy,
          sortDir
        )}`,
        sorter: (a: PlanProductLineItem, b: PlanProductLineItem) =>
          (a.productRateValue || 0) - (b.productRateValue || 0),
        sortOrder: sortBy === 'rate' && sortDir,
        render: (_: unknown, product: PlanProductLineItem) => (
          <div className="text-dark-grey font-medium text-right">
            {product.productRateValue ? (
              <>
                <FormattedNumber roundedTo={2}>
                  {product.productRateValue}
                </FormattedNumber>
                <span className="ml-1">
                  {`${getAbbreviatedUnit(product.productRateUnit)}/ac`}
                </span>
              </>
            ) : (
              NOT_AVAILABLE
            )}
          </div>
        )
      },
      {
        title: <span className="block w-full text-right">Total Product</span>,
        dataIndex: 'totalProduct',
        key: 'totalProduct',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'totalProduct',
          sortBy,
          sortDir
        )}`,
        sorter: (a: PlanProductLineItem, b: PlanProductLineItem) =>
          (a.totalProductValue || 0) - (b.totalProductValue || 0),
        sortOrder: sortBy === 'totalProduct' && sortDir,
        render: (_: unknown, product: PlanProductLineItem) => (
          <div className="text-dark-grey font-medium text-right">
            {product.totalProductValue ? (
              <>
                <FormattedNumber roundedTo={2}>
                  {product.totalProductValue}
                </FormattedNumber>
                <span className="ml-1">
                  {getAbbreviatedUnit(product.totalProductUnit)}
                </span>
              </>
            ) : (
              NOT_AVAILABLE
            )}
          </div>
        )
      },
      {
        title: <span className="block w-full text-right">Appl. Acres</span>,
        dataIndex: 'appliedArea',
        key: 'appliedArea',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'appliedArea',
          sortBy,
          sortDir
        )}`,
        sorter: (a: PlanProductLineItem, b: PlanProductLineItem) =>
          (a.areaValue || 0) - (b.areaValue || 0),
        sortOrder: sortBy === 'appliedArea' && sortDir,
        render: (_: unknown, product: PlanProductLineItem) => (
          <div className="text-right">
            <div className="text-neutral-90 font-medium">
              <FormattedNumber roundedTo={2}>
                {product.areaValue}
              </FormattedNumber>
              <span className="ml-1">ac</span>
            </div>
            <div className="text-neutral-60 text-xs">
              {`${(product.coveragePercent || 0).toFixed(2)}%`}
            </div>
          </div>
        )
      },
      {
        title: <span className="block w-full text-right">Cost</span>,
        dataIndex: 'cost',
        key: 'cost',
        width: 150,
        className: `justify-end text-right ${getHeaderSortClassName(
          'cost',
          sortBy,
          sortDir
        )}`,
        sorter: (a: PlanProductLineItem, b: PlanProductLineItem) =>
          (a.totalProductCost || 0) - (b.totalProductCost || 0),
        sortOrder: sortBy === 'cost' && sortDir,
        render: (_: unknown, product: PlanProductLineItem) =>
          Number(product.totalProductCost) ? (
            <div className="text-right">
              <div className="text-neutral-90 font-medium">
                <FormattedNumber unit="usCurrency">
                  {product.totalProductCost}
                </FormattedNumber>
              </div>
              {Boolean(Number(product.specificCostValue)) && (
                <div className="text-neutral-60 text-xs">
                  <FormattedNumber unit="usCurrency">
                    {product.specificCostValue}
                  </FormattedNumber>
                  <span>
                    {`/${getAbbreviatedUnit(product.specificCostUnit)}`}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-right">{NOT_AVAILABLE}</div>
          )
      },
      {
        title: '',
        dataIndex: 'viewMore',
        key: 'viewMore',
        width: 123,
        className: 'application-view-product-link-cell',
        render: (_: unknown, product: PlanProductLineItem) => (
          <ViewMoreCell
            product={product}
            onViewMore={() => setProductUnderView(product)}
          />
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortBy, sortDir]
  );

  // FR-091: `trackingId` (falling back to `productId`) is the real per-row
  // identity once duplicate products are allowed — matches the wizard's own
  // `ProductsTable.tsx` `lineItemKey`.
  const rowKey = (item: PlanProductLineItem): string =>
    item.trackingId || item.productId;

  const tableScrollConfig = useTableScrollConfig({
    x: PRODUCTS_TABLE_SCROLL_X,
    rowCount: productRowCountOnPage,
    desktopOffsetEm: PRODUCTS_TABLE_OFFSET_EM,
    leadingHeight: PRODUCTS_TABLE_LEADING_HEIGHT,
    wrapperSelector: OPERATION_DETAILS_TABS_WRAPPER_SELECTOR
  });

  return (
    <div className="px-6 py-8" data-testid="plan-products">
      {products.length === 0 ? (
        <div className="bg-white w-full">
          <EmptyTableState
            title="No Products Added"
            containerClassName="w-full h-full pt-16px pb-32px flex flex-col justify-center items-center"
          />
        </div>
      ) : (
        <div
          className={`application-view-products-tab-table plan-products-table-wrapper${
            hasPaginationNavigation
              ? ''
              : ' application-view-products-tab-table-no-pagination'
          }`}
        >
          <Table
            rowKey={rowKey}
            columns={columns}
            tableLayout="fixed"
            scroll={tableScrollConfig}
            pagination={reactUiKitTablePaginationProp}
            onChange={reactUiKitTableOnChange}
            dataSource={products}
            data-testid="plan-products-table"
          />
        </div>
      )}
      <PlanProductViewMoreDrawer
        open={!!productUnderView}
        product={productUnderView}
        onClose={() => setProductUnderView(null)}
      />
    </div>
  );
};

export default PlanProductsTab;
